import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import RoleCreateEditModal from './RoleCreateEditModal';
import ConfirmModal from '../../../../components/ConfirmModal/ConfirmModal';
import { useLanguage } from '../../../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../../../utils/createScopedI18n';
import {
  getPermissionGroupsTreeById,
  getPermissionGroupsTree,
  getPermissionDetail,
  getPagedRoles,
  createRole,
  editRoleDescription,
  editRolePermissions,
  deleteRole,
} from '../../../../services/roleServices';
import usePermissionsConfiguration from 'permissions/modules/configuration/usePermissionsConfiguration';
import { rolesI18n } from './i18n';
import { useSnackbar } from 'context/SnackbarContext';

const Roles = ({ DesignSystemComponents }) => {
  const { language } = useLanguage();
  const rolesLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: rolesI18n });

  const { Card, Button, Input, Table, IconButton, Modal, SvgIcon } =
    DesignSystemComponents;

  const { showSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(false);

  const [totalRoles, setTotalRoles] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [rolesTablePage, setRolesTablePage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [searchDraft, setSearchDraft] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [sortKey, setSortKey] = useState('id');
  const [sortDirection, setSortDirection] = useState('asc');

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  const { canCreateRole, canEditRole, canDeleteRole } =
    usePermissionsConfiguration();

  const debounceRef = useRef(null);

  useEffect(() => {
    rolesI18n.changeLanguage(rolesLanguage);
  }, [rolesLanguage]);

  const handleSearchChange = (text) => {
    setSearchDraft(text);
    setLoading(true);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setRolesTablePage(0);
      setSearchTerm(text.trim());
    }, 350);
  };

  const fetchRoles = async () => {
    try {
      setLoading(true);

      const result = await getPagedRoles.getRoles(
        rolesTablePage + 1,
        pageSize,
        searchTerm,
        sortKey,
        sortDirection
      );

      setTotalRoles(result.items);
      setTotalCount(result.total);
    } catch (error) {
      console.warn('Error loading roles:', error);
      setTotalRoles([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleServerSortChange = ({ sortKey: key, direction }) => {
    setSortKey(key);
    setSortDirection(direction);
    setRolesTablePage(0);
  };

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolesTablePage, pageSize, searchTerm, sortKey, sortDirection]);

  const openCreate = async () => {
    try {
      const permissionsTree = await getPermissionGroupsTree.getTree();

      setSelectedRole({
        name: '',
        description: '',
        status: 'active',
        permissionsTree,
      });

      setShowCreate(true);
    } catch (error) {
      console.error(error);
      showSnackbar(t('roles_error_load_permissions'), 'error');
    }
  };

  const openEdit = async (row) => {
    try {
      setLoading(true);

      const permissionsDetail = await getPermissionDetail.getDetail(row.id);

      const permissionsTree = await getPermissionGroupsTreeById.getTree(row.id);

      setSelectedRole({
        id: permissionsDetail.id,
        name: permissionsDetail.name,
        description: permissionsDetail.description,
        status: permissionsDetail.isEnabled ? 'active' : 'inactive',
        users: permissionsDetail.userCount,
        permissionsTree,
      });

      setShowEdit(true);
    } catch (error) {
      console.error(error);
      showSnackbar(t('roles_error_load_role'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const openDelete = (row) => {
    setSelectedRole(row);
    setShowDelete(true);
  };

  const handleCreate = async (payload) => {
    try {
      setLoading(true);

      await createRole.postCreate({
        name: payload.name,
        description: payload.description,
        permissionIds: payload.permissionIds,
      });

      setShowCreate(false);
      showSnackbar('Rol creado exitosamente', 'success');
      await fetchRoles();
    } catch (error) {
      console.error('Error creating role:', error);
      showSnackbar(t('roles_error_create'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (payload) => {
    if (!selectedRole) return;

    try {
      setLoading(true);

      await editRoleDescription.putEditDescription({
        roleId: selectedRole.id,
        name: payload.name,
        description: payload.description,
        isEnabled: payload.status === 'active',
      });

      await editRolePermissions.putEditPermissions({
        roleId: selectedRole.id,
        permissionIds: payload.permissionIds,
      });

      setShowEdit(false);
      setSelectedRole(null);

      showSnackbar('Rol actualizado exitosamente', 'success');

      await fetchRoles();
    } catch (error) {
      console.error('Error updating role:', error);
      showSnackbar(t('roles_error_update'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole) return;

    try {
      setLoading(true);
      await deleteRole.deleteById(selectedRole.id);

      setShowDelete(false);
      setSelectedRole(null);

      showSnackbar('Rol eliminado exitosamente', 'success');

      if (totalRoles.length === 1 && rolesTablePage > 0) {
        setRolesTablePage((p) => p - 1);
      } else {
        await fetchRoles();
      }
    } catch (error) {
      console.error('Error deleting role:', error);
      showSnackbar(t('roles_error_delete'), 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!Card || !Button || !Input || !Table || !Modal) return null;

  return (
    <Card specialPadding='p-4'>
      <div className='d-flex justify-content-between mb-3'>
        <div>
          <h2 className='mb-1'>{t('roles_title')}</h2>
          <p className='caption font-light'>{t('roles_subtitle')}</p>
        </div>
        <Button
          label={t('roles_new_role')}
          size='sm'
          variant='primary'
          iconStart='plusLg'
          onClick={openCreate}
          disabled={loading || !canCreateRole}
        />
      </div>
      <div className='mb-3'>
        <Input
          icon='search'
          placeholder={t('roles_search_placeholder')}
          type='text'
          value={searchDraft}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>
      <div className='table-container roles-table'>
        <Table
          columns={[
            {
              label: t('roles_col_status'),
              sort: true,
              render: (row) => (
                <SvgIcon
                  name='circleFill'
                  size='14px'
                  fill={
                    row.isEnabled ? 'var(--success-400)' : 'var(--warning-400)'
                  }
                />
              ),
            },
            { accessor: 'name', label: t('roles_col_name'), sort: true },
            {
              accessor: 'description',
              label: t('roles_col_description'),
              sort: true,
            },
            {
              accessor: 'userCount',
              label: t('roles_col_users'),
              sort: true,
            },
            {
              label: t('roles_col_actions'),
              render: (row) => (
                <div className='d-flex gap-2'>
                  <IconButton
                    ariaLabel={t('roles_action_edit')}
                    icon='pencil'
                    onClick={() => openEdit(row)}
                    size='md'
                    iconStroke='var(--primary-500)'
                    disabled={loading || !canEditRole}
                  />
                  {row.userCount === 0 && (
                    <IconButton
                      ariaLabel={t('roles_action_delete')}
                      icon='trash'
                      onClick={() => openDelete(row)}
                      size='md'
                      iconStroke='var(--error-500)'
                      disabled={loading || !canDeleteRole}
                    />
                  )}
                </div>
              ),
            },
          ]}
          data={totalRoles}
          paginator
          loading={loading}
          paginationMode='server'
          page={rolesTablePage}
          pageSize={pageSize}
          totalItems={totalCount}
          onPageChange={setRolesTablePage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setRolesTablePage(0);
          }}
          paginatorGroup={[
            { label: '10', value: 10 },
            { label: '20', value: 20 },
            { label: '50', value: 50 },
            { label: '100', value: 100 },
          ]}
          serverSortKey={sortKey}
          serverSortDirection={sortDirection}
          onServerSortChange={handleServerSortChange}
        />
      </div>
      <RoleCreateEditModal
        DesignSystemComponents={DesignSystemComponents}
        show={showCreate}
        mode='create'
        initialData={selectedRole}
        onClose={() => {
          setShowCreate(false);
          setSelectedRole(null);
        }}
        onSave={handleCreate}
      />
      <RoleCreateEditModal
        DesignSystemComponents={DesignSystemComponents}
        show={showEdit}
        mode='edit'
        initialData={selectedRole}
        onClose={() => {
          setShowEdit(false);
          setSelectedRole(null);
        }}
        onSave={handleEdit}
      />
      <ConfirmModal
        Modal={Modal}
        show={showDelete}
        title={t('roles_confirm_delete_title')}
        message={t('roles_confirm_delete_message', {
          name: selectedRole?.name || t('roles_confirm_delete_fallback_name'),
        })}
        confirmLabel={t('roles_confirm_delete_button')}
        cancelLabel={t('roles_cancel')}
        confirmVariant='danger'
        onConfirm={handleDelete}
        onClose={() => {
          setShowDelete(false);
          setSelectedRole(null);
        }}
      />
    </Card>
  );
};

export default Roles;
